import { useCallback, useEffect, useMemo, useState } from 'react'

import { useRestaurant } from '../../components/common/restaurant_context/RestaurantContext'
import { SiteRestaurantLocation } from '../types'
import {
  addDistanceToLocations,
  sortLocationsByDistance,
  type LocationCoordinates,
  type LocationWithDistance
} from '../../utils/addressUtils'

export type UseLocationSortingOptions = {
  // Whether to automatically request user location when multiple locations exist
  autoRequestLocation?: boolean
}

export type UseLocationSortingReturn = {
  // Locations sorted by distance (closest first)
  sortedLocations: LocationWithDistance[]
  // User's current coordinates (if available)
  userLocation: LocationCoordinates | null
  // Reference coordinates used for distance calculation
  referenceCoords: LocationCoordinates | null
  // Function to manually request user location
  requestUserLocation: () => void
}

/**
 * Custom hook that handles location-based sorting for location picker components
 *
 * Priority for reference coordinates:
 * 1. User's browser location (if available)
 * 2. Current restaurant location
 * 3. No reference (fallback to name-based sorting)
 */
export const useLocationSorting = (
  locations: SiteRestaurantLocation[] | null | undefined,
  options: UseLocationSortingOptions = {}
): UseLocationSortingReturn => {
  const { autoRequestLocation = false } = options
  const { selectedLocation: currentRestaurantLocation } = useRestaurant()
  const [userLocation, setUserLocation] = useState<LocationCoordinates | null>(
    null
  )

  // Function to manually request user location
  const requestUserLocation = useCallback(() => {
    if (
      locations &&
      locations.length > 1 &&
      !userLocation &&
      typeof navigator !== 'undefined' &&
      'geolocation' in navigator
    ) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
        },
        () => {
          // Ignore errors - we have fallback to restaurant location
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      )
    }
  }, [locations, userLocation])

  // Automatically request user location when component mounts (if multiple locations exist)
  useEffect(() => {
    if (
      autoRequestLocation &&
      locations &&
      locations.length > 1 &&
      !userLocation &&
      typeof navigator !== 'undefined' &&
      'geolocation' in navigator
    ) {
      // Small delay to avoid blocking the initial render
      const timer = setTimeout(() => {
        requestUserLocation()
      }, 100)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [autoRequestLocation, locations, requestUserLocation, userLocation])

  // Determine reference coordinates for distance calculation
  const referenceCoords: LocationCoordinates | null = useMemo(() => {
    // User's browser location (if available)
    if (userLocation) {
      return userLocation
    }

    // Current restaurant location
    if (currentRestaurantLocation?.lat && currentRestaurantLocation?.long) {
      return {
        lat: currentRestaurantLocation.lat,
        lng: currentRestaurantLocation.long
      }
    }

    return null
  }, [userLocation, currentRestaurantLocation])

  // Sort locations by distance
  const sortedLocations = useMemo(() => {
    if (!locations?.length) return []

    const locationsWithDistance = addDistanceToLocations(
      locations,
      referenceCoords
    )
    return sortLocationsByDistance(locationsWithDistance)
  }, [locations, referenceCoords])

  return {
    sortedLocations,
    userLocation,
    referenceCoords,
    requestUserLocation
  }
}
